import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LIBERTY_ROLES } from '@/constants/roles'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { isSecurityNotificationPending } from '@/lib/auth/securityNotificationFlag'
import {
  dismissSecurityNotificationResult,
  ensureSecurityNotificationCheck,
  getSecurityCheckLastResult,
} from './securityNotificationCheck'

/** Only show after post-login shell routes (never during /login role picker). */
function isPostLoginShellPath(pathname: string): boolean {
  return (
    pathname === '/dashboard' ||
    pathname.startsWith('/dashboard/') ||
    pathname === '/applicant' ||
    pathname.startsWith('/applicant/')
  )
}

/**
 * Angular `LayoutComponent.checkSecurityNotification` parity:
 * once after login for Liberty roles when config Show = ON — only on dashboard shell.
 */
export function useSecurityNotification() {
  const location = useLocation()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const [message, setMessage] = useState<string | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [])

  const dismiss = useCallback(() => {
    stopCountdown()
    // Clear session cache first so the effect below cannot rehydrate the toast
    // when `message` goes null (message is in the effect dep list).
    dismissSecurityNotificationResult()
    setMessage(null)
    setRemainingSeconds(0)
  }, [stopCountdown])

  const startCountdownTo = useCallback(
    (endsAt: number, content: string) => {
      stopCountdown()
      const tick = () => {
        const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000))
        setRemainingSeconds(left)
        if (left <= 0) {
          stopCountdown()
          setMessage(null)
        }
      }
      setMessage(content)
      tick()
      countdownRef.current = setInterval(tick, 1000)
    },
    [stopCountdown],
  )

  useEffect(() => {
    if (!isAuthenticated || !user?.current_role?.role_name) return
    if (!isPostLoginShellPath(location.pathname)) return

    const roleName = user.current_role.role_name
    const isLiberty = (LIBERTY_ROLES as readonly string[]).includes(roleName)
    if (!isLiberty) return

    const cached = getSecurityCheckLastResult()
    const pending = isSecurityNotificationPending()
    // Need either a pending check (new login) or a cached show (remount after Strict Mode).
    if (!pending && !(cached && cached.kind === 'show' && cached.endsAt > Date.now())) {
      return
    }

    // Already showing this toast — don't restart countdown on in-shell route changes
    // (e.g. dashboard → profile).
    if (message && cached?.kind === 'show' && cached.endsAt > Date.now()) {
      return
    }

    let alive = true

    void ensureSecurityNotificationCheck().then((result) => {
      if (!alive) return
      if (result.kind !== 'show') return
      if (result.endsAt <= Date.now()) return
      // Still on dashboard/applicant shell when result arrives.
      if (!isPostLoginShellPath(window.location.pathname)) return
      startCountdownTo(result.endsAt, result.content)
    })

    return () => {
      alive = false
      // Don't stopCountdown here: Strict Mode remount + in-shell navigations must
      // not tear down a valid visible toast; unmount of the layout still cleans up
      // via the effect below when the component is truly gone.
    }
  }, [
    isAuthenticated,
    user?.current_role?.role_name,
    location.pathname,
    message,
    startCountdownTo,
  ])

  useEffect(() => {
    return () => {
      stopCountdown()
    }
  }, [stopCountdown])

  return {
    message,
    remainingSeconds,
    dismiss,
  }
}
